import { Field, useFormikContext } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { queryStidDetails } from '../../services/mobileAccountService';
import ActivityFrame from '../activity-frame/activity-frame';
import { useDialog } from '../dialog/DialogContext';
import Label from '../label/label';

interface AccountDetailProps {
    isLicensed: boolean, 
    isAccountPopulated: boolean,
    currentItem: any,
}

export const StIdMobileAccountDetails: FC<AccountDetailProps> = ({ isLicensed, isAccountPopulated, currentItem }) => {
    const { translate } = useLocale();
    const { values } = useFormikContext<any>();
    const [creditsAvailable, setCreditsAvailable] = useState<String>();
    const [siteId, setSiteId] = useState<String>();
    const [configId, setConfigId] = useState<String>();
    const [busy, setBusy] = useState(false);
    const dialog = useDialog();
    const { dirty } = useFormikContext<any>();

    useEffect(() => {
        if (currentItem?.details) {
            setSiteId(currentItem.details.siteId.value);
            setConfigId(currentItem.details.readerConfigId.value);
            setCreditsAvailable(currentItem.details.credentialsAvailable.value);
        }
    }, [currentItem]);

    /**
    * The account is queried using saved account details only. Therefore, to query the account:
    * 1. Must be licensed.
    * 2. The account details must be populated.
    * 3. There must be no unsaved changes.
    * 4. The account must be enabled.
    * @returns 
    */
    const canQueryAccount = () => {
        return isLicensed && isAccountPopulated && !dirty && currentItem?.enabled;
    }

    /**
     * For v1.5 only. Get the siteId, readerConfigurationId and avaialble credits in one endpoint call.
     * For future versions this needs to be queried separately so the user can select a site, etc.
     * @param event 
     */
    const onQueryStidDetails = (event) => {
        setBusy(true);
        queryStidDetails(values.details.partNumber.value, translate).then(response => {
            setCreditsAvailable(response.creditsAvailable);
            setSiteId(response.siteId);
            setConfigId(response.readerConfigurationId);
            setBusy(false);
        }).catch((errorResult) => {
            setBusy(false);
            setCreditsAvailable('');
            setSiteId('');
            setConfigId('');
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(errorResult.messageKey) });
        })
    };


    return <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
        {/* Site id */}
        <div className="flex flex-row">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='details.siteId.value'
                    className="text-xs text-gray-600">{translate.byKey(TranslationKeys.site)}:</Label>
                <Field
                    disabled={true} // v1.5 does not yet support selection. Just auto selects first item.
                    name="details.siteId.value"
                    valueName="details.siteId.value"
                    value={siteId}
                    type="text" // Temporary until we have the list of sites
                    className="flex flex-row items-center p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                {/* Placeholder to give extra empty column */}
            </div>
        </div>
        {/* Reader configuration id */}
        <div className="flex flex-row mt-2">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='details.readerConfigId.value'
                    className="text-xs text-gray-600">{translate.byKey(TranslationKeys.configuration)}:</Label>
                <Field
                    disabled={true} // v1.5 does not yet support selection. Just auto selects first item.
                    name="details.readerConfigId.value"
                    valueName="details.readerConfigId.value"
                    value={configId}
                    type="text" // Temporary until we have the list of configurations
                    className="flex flex-row items-center p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                {/* Placeholder to give extra empty column */}
            </div>
        </div>
        {/* Credits available */}
        <div className="flex flex-row mt-2">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='creditsAvailable'
                    className="text-xs text-gray-600">{translate.byKey(TranslationKeys.creditsAvailable)}:</Label>
                <Field
                    disabled={true}
                    name="creditsAvailable"
                    type="text"
                    value={creditsAvailable}
                    className="flex flex-row items-center p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                {/* Placeholder to give extra empty column */}
            </div>
        </div>

        {/* Query account details */}
        {<div className="flex w-full py-4">
            <button
                type='button'
                className={!canQueryAccount()
                    ? " secondary secondary-disabled cursor-not-allowed rounded-lg shadow-lg h-8 px-2 mt-1"
                    : " h-8 px-2 mt-1 secondary bg-sidemenu-focus text-white rounded-lg shadow-lg"}
                disabled={!canQueryAccount()}
                onClick={onQueryStidDetails}
                id="testQueryButton">
                <span className="mr-2">{translate.byKey(TranslationKeys.query)}</span>
            </button>
        </div>}
    </ActivityFrame>
};
